import { removeHtmlTag } from '@exam-management/common/utils';
import { z } from 'zod';

const TASK_NAME_MAX_LENGTH = 256;

const QuestionCountSchema = z.preprocess(
  (value) => {
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return value;
  },
  z
    .number({ message: 'กรุณากรอกจำนวนข้อสอบ' })
    .min(1, { message: 'จำนวนข้อสอบต้องมากกว่า 0' })
    .nullable(),
);

export const ITEM_SET_SIZES = [2, 3, 4, 5] as const;

const ItemSetQuestionCountSchema = z.record(z.enum(['2', '3', '4', '5']), QuestionCountSchema);

export type ItemSetQuestionFormOutput = z.output<typeof ItemSetQuestionCountSchema>;

export const QuestionCreationTaskCreationFormSchema = z.object({
  taskName: z
    .string()
    .min(1, 'กรุณากรอก Task Name')
    .max(TASK_NAME_MAX_LENGTH, 'กรุณากรอก Task Name ไม่เกิน 256 ตัวอักษร'),
  assignee: z
    .object(
      {
        label: z.string(),
        value: z.string(),
      },
      { message: 'กรุณาเลือก Assignee' },
    )
    .nullable()
    .refine((val) => val !== null, { message: 'กรุณาเลือก Assignee' }),
  dueDate: z
    .date({ message: 'กรุณาเลือก Due Date' })
    .nullable()
    .refine((val) => val !== null, { message: 'กรุณาเลือก Due Date' }),
  requirement: z
    .object({
      qBankStructure: z
        .object(
          {
            label: z.string(),
            value: z.string(),
            inputValue: z.string().nullish(),
          },
          { message: 'กรุณาเลือกคลังข้อสอบ' },
        )
        .nullable()
        .refine((val) => val !== null, { message: 'กรุณาเลือกคลังข้อสอบ' }),
      details: z.string().refine(
        (value) => {
          const details = removeHtmlTag(value, {
            customTag: 'p',
          }).trim();
          return details.length >= 1;
        },
        { message: 'กรุณากรอกรายละเอียด' },
      ),
      questionCount: z
        .object({
          single: QuestionCountSchema,
          itemSet: ItemSetQuestionCountSchema,
        })
        .refine(
          (val) => {
            // Validate that at least one of single or itemSet has a value
            const isSingleValid = val.single !== null;
            const isItemSetValid =
              Object.values(val.itemSet).some((count) => count !== null) &&
              Object.keys(val.itemSet).length > 0;

            return isSingleValid || isItemSetValid;
          },
          {
            message: 'กรุณาระบุจำนวนอย่างน้อย 1 ประเภท',
          },
        ),
    })
    .refine((val) => val !== undefined),
});

export type QuestionCreationTaskCreationFormInput = z.input<
  typeof QuestionCreationTaskCreationFormSchema
>;

export type QuestionCreationTaskCreationFormOutput = z.output<
  typeof QuestionCreationTaskCreationFormSchema
>;
